/**
 * Cliente HTTP da API.
 *
 * Traduz o envelope de erro do backend — `{"error": {code, message, details}}` —
 * em uma exceção tipada. Assim toda tela decide o que fazer olhando `code`, e
 * não fazendo correspondência de texto em mensagem.
 */

import { getAccessToken } from "./token";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";

export interface ApiErrorDetail {
  field: string;
  message: string;
}

interface ApiErrorEnvelope {
  error: {
    code: string;
    message: string;
    details?: ApiErrorDetail[];
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: ApiErrorDetail[];
  /** Corpo cru da resposta. Nem toda falha vem no envelope: `/ready` responde
   *  503 com o relatório de diagnóstico, e quem chama pode querer exibi-lo. */
  readonly payload: unknown;

  constructor(
    status: number,
    code: string,
    message: string,
    details: ApiErrorDetail[] = [],
    payload: unknown = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.payload = payload;
  }
}

function isErrorEnvelope(value: unknown): value is ApiErrorEnvelope {
  if (typeof value !== "object" || value === null || !("error" in value)) return false;
  const inner = (value as { error: unknown }).error;
  return typeof inner === "object" && inner !== null && "code" in inner && "message" in inner;
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, ...rest } = options;

  const token = getAccessToken();

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...rest,
    credentials: "include", // o refresh trafega em cookie httpOnly
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // 204 e afins não têm corpo para interpretar.
  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    if (isErrorEnvelope(payload)) {
      throw new ApiError(
        response.status,
        payload.error.code,
        payload.error.message,
        payload.error.details ?? [],
        payload,
      );
    }
    throw new ApiError(
      response.status,
      "UNEXPECTED_ERROR",
      `Falha na requisição (HTTP ${response.status}).`,
      [],
      payload,
    );
  }

  return payload as T;
}
